import React, { Component, Fragment } from 'react';
import { Button, Grid } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { getFormValues, change, untouch } from 'redux-form';
import moment from 'moment';

import { SecurityPreferences } from 'Common/Security/SecurityPreferences';
import { PreferencesEnum as PrefEnum } from 'Common/Security/PreferencesEnum';
import { getId as getUserId } from 'Common/Security/state/selectors';
import { Filter, Main } from 'Containers/';
import { TitledCard } from 'Components/';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import CashCalculator from 'Common/Components/CashCalculator/CashCalculator';
import { formatToCash } from 'Common/FormHelper';
import { MEAL_SUMMARY_FORM_NAME } from 'Common/FormEnum';
import { MealTypesEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { ReportGeneration } from 'Common/ObjectHelper';
import { MealSummaryFieldEnum as Fields } from 'Common/FieldEnum';
import { LoadingPage } from 'Components/';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { errorToast, createToast, toastType } from 'Common/ToastHelper';

import MealSummaryForm from './components/MealSummaryForm';
import {
  writeMealSummary,
  getSummaryAmount,
  updateOpeningCashCalc,
  updateClosingCashCalc,
  getOpeningChecks,
  getClosingChecks,
  clearErrors,
  clearState,
  getUsersAndSummaryAmount,
} from './state/actions';
import * as selectors from './state/selectors';
import { OPENING_CASH_CALCULATOR, CLOSING_CASH_CALCULATOR } from './constants';
import { INITIAL_DENOMINATIONS_STATE } from 'Common/Components/CashCalculator/constants';
import { DOMHelpers } from 'Common/Helpers';

class MealSummaryContainer extends Component {
  static path = '/mealsummary';
  cashCalculatorRef = React.createRef();
  mainSection = null;

  state = {
    activeCashCalculator: null,
  };

  componentDidMount() {
    this.mainSection = document.getElementById('main-section');
  }

  async componentDidUpdate(prevProps, prevState) {
    const {
      summary: prevSummary,
      amount: prevAmount,
      formValues: prevFormValues,
      openingCashCalc: prevOpeningCashCalc,
      closingCashCalc: prevClosingCashCalc,
      users: prevUsers,
    } = prevProps;
    const {
      change,
      summary,
      formValues,
      getSummaryAmount,
      amount,
      clearErrors,
      dayClosed,
      openingCashCalc,
      closingCashCalc,
      clearState,
      summaryRan,
      users,
      userId,
      untouch,
      getUsersAndSummaryAmount,
    } = this.props;

    if (errorToast(this.props, prevProps, 'error')) {
      clearErrors();
    }

    if (prevSummary !== summary && summary) {
      ReportGeneration(summary, 'Meal Summary');
    }

    if (prevFormValues !== formValues) {
      const {
        PrimarySiteId: prevPrimarySiteId,
        ClosingDate: prevClosingDate,
        TypeOfSummary: prevTypeOfSummary,
        UserId: prevUserId,
      } = prevFormValues;
      const { PrimarySiteId, ClosingDate, TypeOfSummary, UserId } = formValues;

      if (prevPrimarySiteId !== PrimarySiteId) {
        // Clear out previous state values
        clearState();
        await getUsersAndSummaryAmount({
          PrimarySiteId,
          ClosingDate,
          TypeOfSummary,
          UserId,
        });

        this.setState({ activeCashCalculator: null });
      }

      // Get summary amount if these fields were changed
      if (
        prevClosingDate !== ClosingDate ||
        prevTypeOfSummary !== TypeOfSummary ||
        prevUserId !== UserId
      ) {
        // Do a truthy check here to avoid having this loaded twice initially
        if (PrimarySiteId) {
          await getSummaryAmount({
            PrimarySiteId,
            ClosingDate,
            TypeOfSummary,
            UserId,
          });
        }

        // Hides the validation error fields
        untouch(MEAL_SUMMARY_FORM_NAME, Fields.OpenAmount);
        untouch(MEAL_SUMMARY_FORM_NAME, Fields.CloseAmount);

        this.setState({ activeCashCalculator: null });
      }
    }

    if (prevUsers !== users) {
      // Check to see if the current user option is in the list of users, default
      // to logged in user if not
      const { UserId } = formValues;
      const containsUser = users.findIndex(user => user.value === UserId) > -1;

      if (!containsUser) {
        change(MEAL_SUMMARY_FORM_NAME, Fields.UserId, userId);
      }
    }

    if (prevAmount !== amount) {
      let openAmount = formatToCash(amount.opening);
      let closeAmount = formatToCash(amount.closing);
      if (amount.opening === 0) {
        if (!dayClosed && !summaryRan) {
          openAmount = '0.00';
        }
      }

      if (amount.closing === 0) {
        if (!dayClosed && !summaryRan) {
          closeAmount = '0.00';
        }
      }

      change(MEAL_SUMMARY_FORM_NAME, Fields.OpenAmount, openAmount);
      change(MEAL_SUMMARY_FORM_NAME, Fields.CloseAmount, closeAmount);
    }

    if (prevOpeningCashCalc !== openingCashCalc) {
      change(
        MEAL_SUMMARY_FORM_NAME,
        Fields.OpeningDenominations,
        openingCashCalc.denominations
      );
      change(
        MEAL_SUMMARY_FORM_NAME,
        Fields.OpeningChecks,
        openingCashCalc.checks
      );
    }

    if (prevClosingCashCalc !== closingCashCalc) {
      change(
        MEAL_SUMMARY_FORM_NAME,
        Fields.ClosingDenominations,
        closingCashCalc.denominations
      );
      change(
        MEAL_SUMMARY_FORM_NAME,
        Fields.ClosingChecks,
        closingCashCalc.checks
      );
    }
  }

  get cashCalculator() {
    const { openingCashCalc, closingCashCalc } = this.props;
    const { activeCashCalculator } = this.state;

    let cashCalculator = null;
    let props = {};

    switch (activeCashCalculator) {
      case OPENING_CASH_CALCULATOR:
        props = {
          onChange: this.onOpeningCalculatorChange,
          denominations: openingCashCalc.denominations,
          checks: openingCashCalc.checks,
          appSidebar: true,
          onGetChecksClick: this.getOpeningChecks,
        };
        break;
      case CLOSING_CASH_CALCULATOR:
        props = {
          onChange: this.onClosingCalculatorChange,
          denominations: closingCashCalc.denominations,
          checks: closingCashCalc.checks,
          appSidebar: true,
          onGetChecksClick: this.getClosingChecks,
        };
        break;
      default:
        break;
    }

    if (activeCashCalculator) {
      cashCalculator = (
        <CashCalculator {...props} containerNode={this.mainSection} />
      );
    }

    return (
      <div ref={this.cashCalculatorRef}>
        {cashCalculator}
      </div>
    );
  }

  get mainProps() {
    const { loading } = this.props;
    return {
      toolbox: {
        formName: MEAL_SUMMARY_FORM_NAME,
        saveButtonProps: {
          content: 'View',
          loading: loading,
          disabled: loading,
        },
        shown: true,
      },
    };
  }

  submitMealSummary = values => {
    const {
      writeMealSummary,
      summaryRan,
      amount,
      setNotificationDialog,
    } = this.props;
    const {
      OpeningDenominations,
      ClosingDenominations,
      ...formValues
    } = values;

    const primarySiteId = formValues[Fields.PrimarySiteId];

    if (!primarySiteId) {
      createToast(toastType.error, 'Error', 'Please select a site.');
      return;
    }

    Object.keys(OpeningDenominations).forEach(k => {
      formValues[`B${k}`] = OpeningDenominations[k];
    });

    Object.keys(ClosingDenominations).forEach(k => {
      formValues[`E${k}`] = ClosingDenominations[k];
    });

    const openingAmount = parseFloat(formValues[Fields.OpenAmount]) || 0;
    const closingAmount = parseFloat(formValues[Fields.CloseAmount]) || 0;

    if (amount.opening !== openingAmount || amount.closing !== closingAmount) {
      if (summaryRan) {
        setNotificationDialog({
          title: 'Summary already ran',
          message: `Summary has already been run with different amounts.
            Amounts previously used were $${formatToCash(amount.opening)} 
            and $${formatToCash(amount.closing)}. Are you sure you want 
            to continue?`,
          showCancel: true,
          actionsToDispatchOnOk: [() => writeMealSummary(formValues)],
        });
        return;
      }
    }
    writeMealSummary(formValues);
  };

  onShowCashCalculator = type => () => {
    const { activeCashCalculator: currentCashCalculator } = this.state;
    const cashCalculatorContainer = this.cashCalculatorRef.current;

    // Hides the cash calculator when it's button is clicked again
    if (currentCashCalculator === type) {
      this.setState({
        activeCashCalculator: null,
      });
    } else {
      this.setState({ activeCashCalculator: type }, () => {
        // Scroll the cash calculator into view if not visible
        const isInViewport = DOMHelpers.isInViewport(cashCalculatorContainer);
        const calculatorRect = cashCalculatorContainer.getBoundingClientRect();

        if (!isInViewport) {
          this.mainSection.scrollBy(0, calculatorRect.height);
        }
      });
    }
  };

  /**
   * Handler to update the opening calculator state in the redux form
   */
  onOpeningCalculatorChange = (total, denominations, checks) => {
    const { change, updateOpeningCashCalc } = this.props;
    change(MEAL_SUMMARY_FORM_NAME, Fields.OpenAmount, formatToCash(total));
    change(MEAL_SUMMARY_FORM_NAME, Fields.OpeningDenominations, denominations);
    change(MEAL_SUMMARY_FORM_NAME, Fields.OpeningChecks, checks);
    updateOpeningCashCalc({
      denominations,
      checks,
    });
  };

  /**
   * Handler to update the closing calculator state in the redux form
   */
  onClosingCalculatorChange = (total, denominations, checks) => {
    const { change, updateClosingCashCalc } = this.props;
    change(MEAL_SUMMARY_FORM_NAME, Fields.CloseAmount, formatToCash(total));
    change(MEAL_SUMMARY_FORM_NAME, Fields.ClosingDenominations, denominations);
    change(MEAL_SUMMARY_FORM_NAME, Fields.ClosingChecks, checks);
    updateClosingCashCalc({
      denominations,
      checks,
    });
  };

  /**
   * Fetches the opening checks for the current summary
   */
  getOpeningChecks = () => {
    const { getOpeningChecks, formValues } = this.props;

    getOpeningChecks({
      [Fields.PrimarySiteId]: formValues[Fields.PrimarySiteId],
      [Fields.ClosingDate]: formValues[Fields.ClosingDate],
      [Fields.TypeOfSummary]: formValues[Fields.TypeOfSummary],
      [Fields.UserId]: formValues[Fields.UserId],
      [Fields.TransactionHistoryOnly]: true,
    });
  };

  /**
   * Fetchest the closing checks for the current summary.
   */
  getClosingChecks = () => {
    const { getClosingChecks, formValues } = this.props;

    getClosingChecks({
      [Fields.PrimarySiteId]: formValues[Fields.PrimarySiteId],
      [Fields.ClosingDate]: formValues[Fields.ClosingDate],
      [Fields.TypeOfSummary]: formValues[Fields.TypeOfSummary],
      [Fields.UserId]: formValues[Fields.UserId],
      [Fields.TransactionHistoryOnly]: true,
    });
  };

  get initialFormValues() {
    const { userId } = this.props;

    return {
      [Fields.TypeOfSummary]: MealTypesEnum.Breakfast,
      [Fields.ClosingDate]: moment().format(DATE_FORMAT_FOR_UI),
      [Fields.OpenAmount]: '',
      [Fields.CloseAmount]: '',
      [Fields.PrimarySiteId]: '',
      [Fields.OpeningChecks]: [],
      [Fields.ClosingChecks]: [],
      [Fields.OpeningDenominations]: INITIAL_DENOMINATIONS_STATE,
      [Fields.ClosingDenominations]: INITIAL_DENOMINATIONS_STATE,
      [Fields.UserId]: userId,
    };
  }

  /**
   * Returns true if opening cash calculator is not empty, false otherwise.
   */
  get isOpenAmountDisabled() {
    const { openingCashCalc } = this.props;

    const isDenominationsEmpty = Object.values(
      openingCashCalc.denominations
    ).every(d => d === 0 || d === '0');
    const isChecksEmpty = openingCashCalc.checks.length === 0;

    return !(isDenominationsEmpty && isChecksEmpty);
  }

  /**
   * Returns true if closing cash calculator is not empty, false otherwise.
   */
  get isCloseAmountDisabled() {
    const { closingCashCalc } = this.props;

    const isDenominationsEmpty = Object.values(
      closingCashCalc.denominations
    ).every(d => d === 0 || d === '0');
    const isChecksEmpty = closingCashCalc.checks.length === 0;

    return !(isDenominationsEmpty && isChecksEmpty);
  }

  /**
   * Determines if the meal summary can be modified after it has
   * been ran
   * @param {Boolean} modifyDisabled true if modify is disabled, false otherwise
   */
  disableModifySummary(modifyDisabled) {
    const { dayClosed, summaryRan } = this.props;

    if (summaryRan && modifyDisabled) {
      return true;
    }

    if (dayClosed) {
      return true;
    }

    return false;
  }

  onSiteChange = value => {
    const { change } = this.props;
    change(MEAL_SUMMARY_FORM_NAME, Fields.PrimarySiteId, value);
  };

  setSectionRef = ref => {
    this.mainSection = ref;
  };

  render() {
    const { loading, userSites, users, dayClosed, summaryRan } = this.props;
    const { activeCashCalculator } = this.state;
    const openCCActive = activeCashCalculator === OPENING_CASH_CALCULATOR;
    const closingCCActive = activeCashCalculator === CLOSING_CASH_CALCULATOR;

    return (
      <SecurityPreferences scope={PrefEnum.ModifyMealSummary}>
        {disabled => {
          return (
            <Fragment>
              {loading && <LoadingPage />}
              <Filter title="Meal Summary" />
              <Main {...this.mainProps} setSectionRef={this.setSectionRef}>
                <TitledCard title="Meal Summary">
                  <MealSummaryForm
                    onSubmit={this.submitMealSummary}
                    loading={loading}
                    userSites={userSites}
                    initialValues={this.initialFormValues}
                    disableOpenAmount={this.isOpenAmountDisabled}
                    disableCloseAmount={this.isCloseAmountDisabled}
                    users={users}
                    dayClosed={dayClosed}
                    summaryRan={summaryRan}
                    modifyDisabled={this.disableModifySummary(disabled)}
                    keyboardAppSidebar
                    onSiteChange={this.onSiteChange}
                  />
                  <Grid>
                    <Grid.Row>
                      <Grid.Column tablet={5} widescreen={4}>
                        <Button
                          type="button"
                          content="Opening Cash Calculator"
                          icon="caret down"
                          labelPosition="right"
                          onClick={this.onShowCashCalculator(
                            OPENING_CASH_CALCULATOR
                          )}
                          disabled={this.disableModifySummary(disabled)}
                          fluid
                          active={openCCActive}
                          color={openCCActive ? 'blue' : null}
                        />
                      </Grid.Column>
                      <Grid.Column tablet={5} widescreen={4}>
                        <Button
                          type="button"
                          content="Closing Cash Calculator"
                          icon="caret down"
                          labelPosition="right"
                          onClick={this.onShowCashCalculator(
                            CLOSING_CASH_CALCULATOR
                          )}
                          disabled={this.disableModifySummary(disabled)}
                          fluid
                          active={closingCCActive}
                          color={closingCCActive ? 'blue' : null}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        {this.cashCalculator}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </TitledCard>
              </Main>
            </Fragment>
          );
        }}
      </SecurityPreferences>
    );
  }
}

const mapStateToProps = state => ({
  summary: selectors.getSummary(state),
  error: selectors.getError(state),
  loading: selectors.getLoading(state),
  amount: selectors.getAmount(state),
  openingCashCalc: selectors.getOpeningCashCalc(state),
  closingCashCalc: selectors.getClosingCashCalc(state),
  formValues: getFormValues(MEAL_SUMMARY_FORM_NAME)(state) || {},
  users: selectors.getUsers(state),
  dayClosed: selectors.getDayClosed(state),
  summaryRan: selectors.getSummaryRan(state),
  userId: getUserId(state),
});

const mapDispatchToProps = {
  writeMealSummary,
  getSummaryAmount,
  change,
  updateOpeningCashCalc,
  updateClosingCashCalc,
  getOpeningChecks,
  getClosingChecks,
  clearErrors,
  clearState,
  setNotificationDialog,
  untouch,
  getUsersAndSummaryAmount,
};

const MealSummary = connect(
  mapStateToProps,
  mapDispatchToProps
)(MealSummaryContainer);

export { MealSummary };



// WEBPACK FOOTER //
// ./src/pages/mealsummary/MealSummary.js